import React, { useEffect, useRef, useState } from "react";

import { useQuery } from "@apollo/client";
import { ArrowBack } from "@mui/icons-material";
import { Box, ButtonBase, CircularProgress, Typography } from "@mui/material";
import { orderBy } from "lodash";
import { useNavigate } from "react-router-dom";
import { withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { NotificationDetailsFragment } from "@/gql/graphql";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { formatISODate } from "@/utils/datetime";

import NotificationTile from "./components/NotificationTile";

const BackButton = withStyles(ButtonBase, {
  root: {
    alignItems: "center",
    display: "flex",
  },
});

const BackButtonIcon = withStyles(ArrowBack, {
  root: {
    height: "12px",
    marginRight: "8px",
    width: "12px",
  },
});

const BackButtonText = withStyles(Typography, {
  root: {
    color: "#1FD4AF",
    fontSize: 12,
    fontWeight: 700,
  },
});

const Container = withStyles(Box, {
  root: {
    margin: "20px",
    overflow: "auto",
  },
});

const NotificationContainer = withStyles(Box, {
  root: {
    backgroundColor: "white",
    padding: "20px",
  },
});

const NotificationTitle = withStyles(Typography, {
  root: {
    fontSize: "26px",
    fontWeight: 700,
  },
});

const LoadingIndicator = withStyles(Box, {
  root: {
    alignItems: "center",
    display: "flex",
    height: "82vh",
    justifyContent: "center",
  },
});

const notificationPageQuery = graphql(`
  query CoachNotification($date: ISO8601Date!, $before: String) {
    coachViewer {
      id
      notifications(last: 20, before: $before) {
        nodes {
          ...NotificationDetails
        }
        pageInfo {
          hasPreviousPage
          startCursor
        }
      }
    }
  }
`);

const NotificationPage: React.FC = () => {
  const lastNotificationTileRef = useRef<HTMLDivElement>(null);
  const [results, setResults] = useState<NotificationDetailsFragment[]>();
  const navigate = useNavigate();
  const hasPreviousPageRef = useRef<boolean>(false);
  const startCursorRef = useRef<string | null | undefined>();
  const isFetchingMoreRef = useRef<boolean>(false);
  const { fetchMore, loading } = useQuery(notificationPageQuery, {
    variables: { date: formatISODate(new Date()) },
    onCompleted: (data) => {
      if (data.coachViewer.notifications == undefined) return;

      const { nodes, pageInfo } = data.coachViewer.notifications;
      setResults(nodes);
      hasPreviousPageRef.current = pageInfo.hasPreviousPage;
      startCursorRef.current = pageInfo.startCursor;
    },
  });

  const notifications = orderBy(results ?? [], ["createdAt"], ["desc"]);

  const handleNavigateBack = () => navigate(-1);

  const fetchMoreNotifications = async () => {
    // Bail out when there is nothing more to load, when the initial query is
    // still loading, or when a previous "fetch more" is already in flight.
    // The intersection observer fires repeatedly while the last tile is on
    // screen, so without these guards the same page gets fetched and appended
    // over and over, producing duplicate notifications.
    if (!hasPreviousPageRef.current || loading || isFetchingMoreRef.current) {
      return;
    }

    isFetchingMoreRef.current = true;
    try {
      const fetchedData = await fetchMore({
        variables: {
          before: startCursorRef.current,
        },
      });
      const { nodes, pageInfo } = fetchedData.data.coachViewer.notifications;

      // Append only notifications we haven't seen yet, as a safety net against
      // overlapping pages returning records already in the list.
      setResults((prev) => {
        const existing = prev ?? [];
        const seenIds = new Set(
          existing.map((notification) => notification.id)
        );
        const freshNodes = nodes.filter(
          (notification) => !seenIds.has(notification.id)
        );
        return existing.concat(freshNodes);
      });
      startCursorRef.current = pageInfo.startCursor;
      hasPreviousPageRef.current = pageInfo.hasPreviousPage;
    } finally {
      isFetchingMoreRef.current = false;
    }
  };

  useIntersectionObserver({
    targetElementRef: lastNotificationTileRef,
    callback: fetchMoreNotifications,
  });

  const renderNotifications = () =>
    loading ? (
      <LoadingIndicator>
        <CircularProgress size={35} />
      </LoadingIndicator>
    ) : (
      notifications?.map((notification, index) => (
        <NotificationTile
          ref={
            index === notifications.length - 1 ? lastNotificationTileRef : null
          }
          key={notification.id}
          notification={notification}
        />
      ))
    );

  return (
    <Container>
      <BackButton disableRipple onClick={handleNavigateBack}>
        <BackButtonIcon color="primary" />
        <BackButtonText>Back</BackButtonText>
      </BackButton>
      <Box marginBottom={4} />
      <NotificationContainer>
        <NotificationTitle>Notifications Center</NotificationTitle>
        {renderNotifications()}
      </NotificationContainer>
    </Container>
  );
};

export default NotificationPage;
